import { Component, effect, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { NewOrganization } from "../../../organization/component/new-organization/new-organization";
import { SelectOrganizationComponent } from "../select-organization/select-organization.component";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { OrganizationsService } from "../../../organization/service/organizations.service";
import { OrganizationDTO } from "../../../organization/dto/OrganizationDTO";
import { ShowUIService } from "../../../../core/service/show-ui.service";
import { ModalEntranceCardComponent } from "../modal-entrance-card/modal-entrance-card.component";
import { UrlService } from "../../../../../environments/urlService";
import { InitStateBusinessService } from "../../../../core/service/init-state-business.service";
import { OrganizationWithAccesses } from "../../../../core/component/hornavbar/dto/InitDataDTO";
import { FRONT_URLS, MODELS_URLS } from "../../../../util/common/url-parts";
import { TooltipComponent } from "../../../shared/component/tooltip/tooltip.component";
import { ToolTipSettings } from "../../../shared/component/tooltip/tooltip.util";

@Component({
  selector: 'app-entrance',
  imports: [
    NewOrganization,
    SelectOrganizationComponent,
    ModalEntranceCardComponent,
    TooltipComponent,
  ],
  templateUrl: './entrance.component.html',
  styleUrl: './entrance.component.scss',
})
export class EntranceComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private organizationsService = inject(OrganizationsService);
  private showUIService = inject(ShowUIService);
  private urlService = inject(UrlService);
  private initService = inject(InitStateBusinessService);

  protected readonly EntranceStates = EntranceStates;
  protected show: EntranceStates;

  organizations: OrganizationWithAccesses[] = [];
  isNewOrgCanBeCreated: boolean = false;

  fontSizes: FontSizes;
  width: number;
  height: number;
  protected readonly FRONT_URLS = FRONT_URLS;
  protected readonly MODELS_URLS = MODELS_URLS;

  private readonly AI_TOOLTIP_TITLE = 'Request the full version to enable AI tools';
  tooltipSettings: ToolTipSettings = {
    show: false,
    title: this.AI_TOOLTIP_TITLE,
  };

  constructor() {
    this.showUIService.setShowHorNavBar(false);
    this.showUIService.setShowSideNavBar(false);
    effect(() => {
      const data = this.initService.currentInitData();
      if (data) {
        this.isNewOrgCanBeCreated = data.isNewOrgCanBeCreated;
        this.organizationsService.setAll(data.organizations);
        this.organizations = data.organizations;
        this.initEntranceState();
      }
    });
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.width = document.documentElement.clientWidth;
    this.height = document.documentElement.clientHeight;
    this.setFontSizes(this.height, this.width);
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  redirectTo(value: FRONT_URLS | MODELS_URLS): void {
    switch (value) {
      case FRONT_URLS.INVESTOR_ONBOARDING:
        window.open(this.urlService.getOnboardingUrl(), '_blank');
        break;
      case MODELS_URLS.BASE:
        const investmentPath = MODELS_URLS.BASE + '/' + MODELS_URLS.INVESTMENT;
        this.showUIService.setShowSideNavBar(true);
        void this.router.navigate([investmentPath], { queryParams: this.activatedRoute.snapshot.queryParams });
        break;
      default:
        void this.router.navigate([value], { queryParams: this.activatedRoute.snapshot.queryParams });
        break;
    }
  }

  activateAiTooltip(id: string): void {
    this.tooltipSettings.coordinates = document.getElementById(id)?.getBoundingClientRect();
    this.tooltipSettings.show = true;
  }

  deactivateAiTooltip(): void {
    this.tooltipSettings.show = false;
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.width = document.documentElement.clientWidth;
    this.height = document.documentElement.clientHeight;
    this.setFontSizes(this.height, this.width);
  }

  setFontSizes(height: number, width: number): void {
    const fontSize = height * 1.62 < width ? height / 72 : width / 125; // very important magic numbers
    this.fontSizes = {
      topBar: width < 479 ? '4.2vw' : fontSize + 'px',
      general: width < 479 ? '1vw' : fontSize + 'px',
      pmd: width < 479 ? '3.4vw' : fontSize * 1.2 + 'px',
    };
  }

  async selectOrganization(org: OrganizationDTO): Promise<void> {
    await this.organizationsService.setSelected(org);
    await this.router.navigate([FRONT_URLS.ENTRANCE], { queryParams: { o: org.uuid } });
    this.show = EntranceStates.ORGANIZATION_IS_SPECIFIED;
  }

  private initEntranceState(): void {
    const organizationUuid = this.activatedRoute.snapshot.queryParamMap.get('o');
    if (organizationUuid) {
      this.showUIService.setShowHorNavBar(true);
      this.showUIService.setShowSideNavBar(false);
      this.show = EntranceStates.ORGANIZATION_IS_SPECIFIED;
    } else if (this.organizations && this.organizations.length === 0) {
      this.showUIService.setShowHorNavBar(false);
      this.showUIService.setShowSideNavBar(false);
      this.show = EntranceStates.CREATE_ORGANIZATION;
    } else {
      this.showUIService.setShowHorNavBar(false);
      this.showUIService.setShowSideNavBar(false);
      this.show = EntranceStates.SELECT_ORGANIZATION;
    }
  }

  private initSubscriptions(): void {
    this.router.events
      .pipe(
        takeUntil(this._destroyStream),
        filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.initEntranceState();
      });
  }
}

enum EntranceStates {
  CREATE_ORGANIZATION = 'CREATE_ORGANIZATION',
  SELECT_ORGANIZATION = 'SELECT_ORGANIZATION',
  ORGANIZATION_IS_SPECIFIED = 'ORGANIZATION_SPECIFIED',
}

interface FontSizes {
  topBar: string;
  general: string;
  pmd: string;
}
